import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Loader, Brain, FileCheck, Building2, Calendar, DollarSign, Scale, Workflow, Info } from 'lucide-react';
import { useUploadDocumentMutation, useGetVendorsQuery } from '../../store/api/apiSlice';
import { useSelector } from 'react-redux';
import { AIValidatedInput } from './AIValidatedInput';
import { AIValidatedInputWithHighlight } from './AIValidatedInputWithHighlight';
import { Stepper } from './Stepper';
import toast from 'react-hot-toast';

interface MultiStepDocumentUploadProps {
  vendorId?: string;
  onUploadComplete?: (document: any) => void;
  onClose?: () => void;
}

interface DetectedField {
  fieldName: string;
  displayName: string;
  value: string;
  confidence?: number;
  sourceContext?: string;
  sourceRange?: [number, number];
  extractionNote?: string;
  category: 'basic' | 'parties' | 'dates' | 'financial' | 'legal' | 'workflow';
}

interface AIDetectedFields {
  basic: DetectedField[];
  parties: DetectedField[];
  dates: DetectedField[];
  financial: DetectedField[];
  legal: DetectedField[];
  workflow: DetectedField[];
}

export const MultiStepDocumentUpload: React.FC<MultiStepDocumentUploadProps> = ({
  vendorId,
  onUploadComplete,
  onClose
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState(vendorId || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [detectedFields, setDetectedFields] = useState<AIDetectedFields>({
    basic: [],
    parties: [],
    dates: [],
    financial: [],
    legal: [],
    workflow: []
  });
  const [extractedText, setExtractedText] = useState<string>('');
  const [editedFields, setEditedFields] = useState<Record<string, string>>({});
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation();
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;
  const { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery({ skip: 0, limit: 100 }, {
    // Skip API call until authentication is ready
    skip: !isAuthReady
  });

  const steps = [
    {
      id: 'upload',
      title: 'Upload Document',
      description: 'Select your contract file',
      icon: <Upload className="w-4 h-4" />
    },
    {
      id: 'processing',
      title: 'AI Processing',
      description: 'Analyzing document content',
      icon: <Brain className="w-4 h-4" />
    },
    {
      id: 'review',
      title: 'Review & Confirm',
      description: 'Verify detected information',
      icon: <FileCheck className="w-4 h-4" />
    }
  ];

  const mockAIDetection = () => {
    setTimeout(() => {
      const mockFields: AIDetectedFields = {
        basic: [
          {
            fieldName: 'title',
            displayName: 'Document Title',
            value: 'Service Agreement - Tech Solutions',
            confidence: 0.95,
            sourceContext: 'SERVICE AGREEMENT BETWEEN ACME CORP AND TECH SOLUTIONS INC',
            sourceRange: [0, 55],
            extractionNote: 'Extracted from document header',
            category: 'basic'
          },
          {
            fieldName: 'contract_type',
            displayName: 'Contract Type',
            value: 'Service Agreement',
            confidence: 0.92,
            sourceContext: 'SERVICE AGREEMENT BETWEEN',
            sourceRange: [0, 16],
            extractionNote: 'Identified contract type from title',
            category: 'basic'
          }
        ],
        parties: [
          {
            fieldName: 'counterparty_name',
            displayName: 'Counterparty Name',
            value: 'Tech Solutions Inc',
            confidence: 0.98,
            sourceContext: 'TECH SOLUTIONS INC, a Delaware corporation',
            sourceRange: [45, 62],
            extractionNote: 'Extracted company name',
            category: 'parties'
          }
        ],
        dates: [
          {
            fieldName: 'execution_date',
            displayName: 'Execution Date',
            value: '2024-01-15',
            confidence: 0.89,
            sourceContext: 'This agreement is executed on January 15, 2024',
            sourceRange: [150, 170],
            extractionNote: 'Date normalized to ISO format',
            category: 'dates'
          }
        ],
        financial: [
          {
            fieldName: 'contract_value',
            displayName: 'Contract Value',
            value: '$50,000',
            confidence: 0.85,
            sourceContext: 'Total contract value of fifty thousand dollars ($50,000)',
            sourceRange: [300, 320],
            extractionNote: 'Amount extracted from written form',
            category: 'financial'
          }
        ],
        legal: [],
        workflow: []
      };
      
      setDetectedFields(mockFields);
      setExtractedText('SERVICE AGREEMENT BETWEEN ACME CORP AND TECH SOLUTIONS INC\n\nThis agreement is executed on January 15, 2024...');
      setIsProcessing(false);
      setCurrentStep(3);
    }, 3000);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    // Check if user is authenticated
    if (!auth.isAuthenticated || !auth.token) {
      toast.error('You must be logged in to upload documents');
      return;
    }

    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      if (selectedVendorId) {
        uploadData.append('vendor_id', selectedVendorId);
      }
      uploadData.append('title', selectedFile.name);

      const result = await uploadDocument(uploadData).unwrap();
      
      toast.success('Document uploaded successfully!');
      setCurrentStep(2);
      setIsProcessing(true);
      mockAIDetection();
      
    } catch (error: any) {
      console.error('Upload error:', error);

      // Handle quota exceeded error specifically
      if (error?.data?.detail?.code === 'DOCUMENT_QUOTA_EXCEEDED') {
        const detail = error.data.detail;
        toast.error(
          `Document quota exceeded. You have ${detail.available} of ${detail.quota} documents remaining.`,
          { duration: 6000 }
        );
      } else {
        toast.error(
          error?.data?.detail?.message || error?.data?.detail || 'Upload failed'
        );
      }
    }
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleFinalize = () => {
    if (onUploadComplete) {
      onUploadComplete({
        id: 'mock-id',
        status: 'completed',
        detectedFields,
        extractedText
      });
    }

    toast.success('Document finalized successfully!');
    if (onClose) onClose();
  };

  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'basic':
        return { icon: <Info className="w-4 h-4" />, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' };
      case 'parties':
        return { icon: <Building2 className="w-4 h-4" />, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' };
      case 'dates':
        return { icon: <Calendar className="w-4 h-4" />, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' };
      case 'financial':
        return { icon: <DollarSign className="w-4 h-4" />, color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20' };
      case 'legal':
        return { icon: <Scale className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' };
      case 'workflow':
        return { icon: <Workflow className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-900/20' };
      default:
        return { icon: <Info className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-50 dark:bg-gray-900/20' };
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Vendor (Optional)
        </label>
        <select
          value={selectedVendorId}
          onChange={(e) => setSelectedVendorId(e.target.value)}
          disabled={isLoadingVendors}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
        >
          <option value="">
            {isLoadingVendors ? 'Loading vendors...' : 'Select a vendor'}
          </option>
          {vendors?.items.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </select>
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
          isDragOver
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <FileText className="w-12 h-12 text-primary-600 dark:text-primary-400" />
              <div className="text-left">
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={removeFile}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Upload className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <p className="text-xl font-medium text-gray-900 dark:text-gray-100">
                Drop your contract here
              </p>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  browse files
                </button>
              </p>
              <p className="text-sm text-gray-400 mt-4">
                Supports PDF, DOCX, TXT files up to 10MB
              </p>
            </div>
          </div>
        )}
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,.docx,.txt"
          className="hidden"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload & Analyze
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
        <Brain className="w-10 h-10 text-primary-600 dark:text-primary-400 animate-pulse" />
      </div>
      
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        AI is analyzing your document
      </h3>
      
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Processing document with artificial intelligence...
      </p>

      <div className="max-w-md mx-auto space-y-3">
        <div className="flex items-center space-x-3 text-sm">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-gray-900 dark:text-gray-100">Document uploaded</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Loader className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-gray-900 dark:text-gray-100">Extracting text content</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Loader className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-gray-900 dark:text-gray-100">Identifying contract terms</span>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <Loader className="w-4 h-4 text-blue-500 animate-spin" />
          <span className="text-gray-900 dark:text-gray-100">Validating extracted data</span>
        </div>
      </div>

      <div className="mt-8">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-500 animate-pulse"
            style={{ width: '60%' }}
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          AI Analysis Complete
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Review and verify the extracted information below
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(detectedFields).map(([category, fields]) => {
          if (fields.length === 0) return null;
          
          const categoryInfo = getCategoryInfo(category);
          
          return (
            <div key={category} className={`rounded-lg border p-6 ${categoryInfo.bg}`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`${categoryInfo.color}`}>
                  {categoryInfo.icon}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 capitalize">
                  {category === 'basic' ? 'Basic Information' : 
                   category === 'parties' ? 'Parties & Contacts' :
                   category === 'dates' ? 'Important Dates' :
                   category === 'financial' ? 'Financial Terms' :
                   category === 'legal' ? 'Legal Clauses' :
                   category === 'workflow' ? 'Workflow & Status' : category}
                </h4>
              </div>
              
              <div className="space-y-4">
                {fields.map((field: DetectedField) => (
                  <div key={field.fieldName}>
                    {field.sourceContext ? (
                      <AIValidatedInputWithHighlight
                        value={editedFields[field.fieldName] || field.value}
                        onChange={(value) => handleFieldChange(field.fieldName, value)}
                        valueContext={extractedText}
                        label={field.displayName}
                        placeholder={`Enter ${field.displayName.toLowerCase()}`}
                        extractionNote={field.extractionNote}
                        highlightRanges={field.sourceRange ? [{ start: field.sourceRange[0], end: field.sourceRange[1] }] : []}
                        initialValueStatus="unverified"
                      />
                    ) : (
                      <AIValidatedInput
                        value={editedFields[field.fieldName] || field.value}
                        onChange={(value) => handleFieldChange(field.fieldName, value)}
                        valueContext={field.sourceContext || ''}
                        label={field.displayName}
                        placeholder={`Enter ${field.displayName.toLowerCase()}`}
                        initialValueStatus="unverified"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between pt-6">
        <button
          onClick={() => setCurrentStep(2)}
          className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          Back to Processing
        </button>
        <div className="space-x-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleFinalize}
            className="btn-primary"
          >
            <FileCheck className="w-4 h-4 mr-2" />
            Finalize Document
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Upload Contract Document
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <Stepper steps={steps} currentStep={currentStep} />

      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </div>
  );
}; 